import { useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import { CancellationRequestDto } from "../../../types/cancellation";
import CompleteCancellationRequestModal from "./CompleteCancellationRequestModal";

type Busy = null | "submit" | "complete" | "revoke" | "sendInsurer";

export default function CancellationRequestActions({
  row,
  token,
  confirm,
  onReload,
}: {
  row: CancellationRequestDto;
  token: string | null;
  confirm: ReturnType<typeof useConfirm>;
  onReload: () => void;
}) {
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);

  const handleSubmit = async () => {
    const ok = await confirm({
      title: "Enviar a Viafirma",
      message:
        "Esto generará el PDF, lo subirá al bucket y enviará al cliente el email de firma con CC a operations. ¿Continuar?",
      confirmLabel: "Enviar",
      tone: "primary",
    });
    if (!ok) return;
    setBusy("submit");
    setError(null);
    try {
      await request(
        `/api/admin/operations/cancellation-requests/${row.id}/submit`,
        { method: "POST", token },
      );
      onReload();
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${
              ((err.body as { message?: string })?.message ?? "").slice(0, 160) ||
              ((err.body as { error?: string })?.error ?? "")
            }`
          : "Envío falló",
      );
    } finally {
      setBusy(null);
    }
  };

  const handleRevoke = async () => {
    const ok = await confirm({
      title: "Revocar solicitud",
      message:
        "Esto cancelará la firma en Viafirma (si aplica) y marcará la solicitud como revocada. La póliza no se cancela. ¿Continuar?",
      confirmLabel: "Revocar",
      tone: "danger",
    });
    if (!ok) return;
    setBusy("revoke");
    setError(null);
    try {
      await request(
        `/api/admin/operations/cancellation-requests/${row.id}/revoke`,
        { method: "POST", token },
      );
      onReload();
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}`
          : "No se pudo revocar",
      );
    } finally {
      setBusy(null);
    }
  };

  const handleSendToInsurer = async () => {
    const alreadySent = row.insurerNotifiedAt
      ? `Ya se envió el ${new Date(row.insurerNotifiedAt).toLocaleString("es-ES")}. `
      : "";
    const ok = await confirm({
      title: row.insurerNotifiedAt
        ? "Reenviar cancelación a AXA"
        : "Enviar cancelación a AXA",
      message:
        `${alreadySent}Se enviará un email a AXA (operations@life5.com como remitente) con la solicitud firmada y el DNI del cliente (anverso y reverso) adjuntos. ¿Continuar?`,
      confirmLabel: row.insurerNotifiedAt ? "Reenviar" : "Enviar",
      tone: row.insurerNotifiedAt ? "warning" : "primary",
    });
    if (!ok) return;
    setBusy("sendInsurer");
    setError(null);
    try {
      await request(
        `/api/admin/operations/cancellation-requests/${row.id}/send-to-insurer`,
        { method: "POST", token },
      );
      onReload();
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${
              ((err.body as { message?: string })?.message ?? "").slice(0, 160) ||
              ((err.body as { error?: string })?.error ?? "")
            }`
          : "No se pudo enviar a AXA",
      );
    } finally {
      setBusy(null);
    }
  };

  const canSubmit = row.status === "DRAFT";
  // Only signed Tipo 1 requests can be forwarded to the carrier (the recesso
  // flow has no signed document to attach).
  const canSendToInsurer = row.status === "SIGNED";
  // RECESSO_PENDING (Italian recesso, no signature) completes the same way as
  // SIGNED: ops confirm the carrier effective date.
  const canComplete =
    row.status === "SIGNED" || row.status === "RECESSO_PENDING";
  const canRevoke =
    row.status === "DRAFT" ||
    row.status === "SIGNATURE_PENDING" ||
    row.status === "SIGNED" ||
    row.status === "RECESSO_PENDING";

  return (
    <div style={{ display: "flex", gap: "0.35rem", alignItems: "center" }}>
      {canSubmit && (
        <ActionButton
          onClick={handleSubmit}
          tone="primary"
          loading={busy === "submit"}
          disabled={!!busy}
        >
          Enviar
        </ActionButton>
      )}
      {canSendToInsurer && (
        <ActionButton
          onClick={handleSendToInsurer}
          tone={row.insurerNotifiedAt ? "neutral" : "primary"}
          loading={busy === "sendInsurer"}
          disabled={!!busy}
        >
          {row.insurerNotifiedAt ? "Reenviar a AXA" : "Enviar a AXA"}
        </ActionButton>
      )}
      {canSendToInsurer && row.insurerNotifiedAt && (
        <span
          style={{ fontSize: ".72rem", color: "#73879C" }}
          title={`Enviada a AXA el ${new Date(row.insurerNotifiedAt).toLocaleString("es-ES")}`}
        >
          Enviada a AXA{" "}
          {new Date(row.insurerNotifiedAt).toLocaleDateString("es-ES")}
        </span>
      )}
      {canComplete && (
        <ActionButton
          onClick={() => setCompleteModalOpen(true)}
          tone="primary"
          disabled={!!busy}
        >
          Completar
        </ActionButton>
      )}
      {row.pdfUrlUnsigned && (
        <a
          href={row.pdfUrlUnsigned}
          target="_blank"
          rel="noreferrer"
          style={linkStyle}
        >
          PDF
        </a>
      )}
      {row.pdfUrlSigned && (
        <a
          href={row.pdfUrlSigned}
          target="_blank"
          rel="noreferrer"
          style={linkStyle}
        >
          Firmado
        </a>
      )}
      {row.viafirmaId && row.status === "SIGNATURE_PENDING" && (
        <span
          style={{
            fontSize: ".72rem",
            color: "#73879C",
            fontFamily: "monospace",
          }}
          title={`Viafirma ${row.viafirmaId}`}
        >
          Esperando firma
        </span>
      )}
      {canRevoke && (
        <ActionButton
          onClick={handleRevoke}
          tone="danger"
          loading={busy === "revoke"}
          disabled={!!busy}
        >
          Revocar
        </ActionButton>
      )}
      {error && (
        <span style={{ color: "#b32424", fontSize: ".72rem" }}>{error}</span>
      )}

      {completeModalOpen && (
        <CompleteCancellationRequestModal
          row={row}
          token={token}
          onClose={() => setCompleteModalOpen(false)}
          onApplied={() => {
            setCompleteModalOpen(false);
            onReload();
          }}
        />
      )}
    </div>
  );
}

function ActionButton({
  onClick,
  tone,
  loading,
  disabled,
  children,
}: {
  onClick: () => void;
  tone: "primary" | "danger" | "neutral";
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const palette =
    tone === "danger"
      ? { bg: "#fde2e2", fg: "#b32424", border: "#f5c6cb" }
      : tone === "primary"
        ? { bg: "#0069d9", fg: "white", border: "#0069d9" }
        : { bg: "white", fg: "#1f2933", border: "#dee2e6" };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "0.3rem 0.65rem",
        borderRadius: "0.35rem",
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        fontSize: ".75rem",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled && !loading ? 0.6 : 1,
      }}
    >
      {loading ? "…" : children}
    </button>
  );
}

const linkStyle: React.CSSProperties = {
  fontSize: ".72rem",
  color: "#0069d9",
  fontWeight: 600,
  textDecoration: "none",
  padding: "0.3rem 0.5rem",
  border: "1px solid #b3d4fc",
  borderRadius: "0.35rem",
  background: "#f0f7ff",
};
